// src/pages/Msb/QpayCenterSettlementsReport.jsx
// Same layout as StateAdminReport/StateHeadSettlementsReport: header, info block, filter bar, summary tiles, grid.
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Chip,
  InputAdornment
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid } from '@mui/x-data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet, faDownload, faRefresh, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import SearchIcon from '@mui/icons-material/Search';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { format } from 'date-fns';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { formatDateTimeIST } from '../../utils/dateFormat';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(amount) || 0);

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const QpayCenterSettlementsReport = () => {
  const { can } = usePermissions();
  const [userId, setUserId] = useState('');
  const [settlements, setSettlements] = useState([]);
  const [centresList, setCentresList] = useState([]);
  const [selectedCentreFilter, setSelectedCentreFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Default date range: last 30 days (Date objects, sent as yyyy-MM-dd)
  const [startDate, setStartDate] = useState(() => daysAgo(30));
  const [endDate, setEndDate] = useState(() => new Date());

  const token = sessionStorage.getItem('token');

  useEffect(() => {
    if (token) {
      try {
        setUserId(jwtDecode(token).userId);
      } catch (err) {
        console.error('Invalid token', err);
      }
    }
  }, [token]);

  // Q-Pay Centres the login may see (drives the dropdown and the info block)
  useEffect(() => {
    if (!userId) return;
    axios
      .post(`${API_BASE_URL}/qpay-centers/all`, { userId })
      .then((res) => {
        if (res.status === 200 && res.data && Array.isArray(res.data.data)) {
          setCentresList(res.data.data.map((c) => ({
            id: c.id,
            customId: c.customId,
            name: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
            mobile: c.mobile || c.phoneNumber || '',
            email: c.email || ''
          })));
        }
      })
      .catch((err) => console.error('Error fetching Q-Pay Centres dropdown:', err));
  }, [userId]);

  const fetchSettlementsReport = useCallback(async () => {
    if (!userId || !startDate || !endDate || isNaN(startDate) || isNaN(endDate)) return;
    try {
      setLoading(true);
      setError(null);
      const res = await axios.post(
        `${API_BASE_URL}/qpay-centers/settlements-report`,
        {
          userId,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          sortOrder: 'desc'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data && res.data.success) {
        setSettlements(res.data.data || []);
      } else {
        setSettlements([]);
        setError('Failed to fetch settlements report.');
      }
    } catch (err) {
      console.error('Error fetching settlements report:', err);
      setError('An error occurred while fetching the settlements report.');
    } finally {
      setLoading(false);
    }
  }, [userId, startDate, endDate, token]);

  useEffect(() => {
    fetchSettlementsReport();
  }, [fetchSettlementsReport]);

  // Centre / search filters
  const filteredSettlements = useMemo(() => {
    let result = settlements;
    if (selectedCentreFilter !== 'ALL') {
      result = result.filter((s) => String(s.qpayCenterId) === selectedCentreFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((s) =>
        [s.qpayCenterCustomId, s.qpayCenterName, s.transactionId, s.remarks]
          .some((v) => String(v || '').toLowerCase().includes(term))
      );
    }
    return result;
  }, [settlements, selectedCentreFilter, searchTerm]);

  const rows = useMemo(
    () => filteredSettlements.map((s, idx) => ({ ...s, id: s.id || idx, serialNo: idx + 1 })),
    [filteredSettlements]
  );

  // Summary tiles
  const summary = useMemo(() => {
    const total = filteredSettlements.reduce((a, s) => a + (Number(s.settledAmount) || 0), 0);
    const centreCount = new Set(filteredSettlements.map((s) => String(s.qpayCenterId))).size;
    return { total, count: filteredSettlements.length, centreCount };
  }, [filteredSettlements]);

  // Info block: selected centre details, else overview
  const centreInfo = useMemo(
    () => (selectedCentreFilter === 'ALL' ? null : centresList.find((c) => String(c.id) === selectedCentreFilter) || null),
    [centresList, selectedCentreFilter]
  );

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('qpayCentre.settlementsReport.export')) return;
    if (filteredSettlements.length === 0) return;

    const headers = ['S.No.', 'Date & Time', 'Q-Pay Centre ID', 'Q-Pay Centre Name', 'Amount (₹)', 'Transaction ID', 'Remarks'];
    const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csvRows = [headers.join(',')];
    filteredSettlements.forEach((s, idx) => {
      csvRows.push([
        idx + 1,
        q(formatDateTimeIST(s.date)),
        q(s.qpayCenterCustomId),
        q(s.qpayCenterName),
        s.settledAmount || 0,
        q(s.transactionId),
        q(s.remarks)
      ].join(','));
    });

    const encodedUri = encodeURI('data:text/csv;charset=utf-8,' + csvRows.join('\n'));
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `QPAY_CENTRE_Settlements_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { field: 'serialNo', headerName: 'S.No.', width: 80, align: 'center', headerAlign: 'center' },
    { field: 'date', headerName: 'Date & Time', width: 200, renderCell: (p) => formatDateTimeIST(p.value) },
    {
      field: 'qpayCenterName',
      headerName: 'Q-Pay Centre',
      width: 220,
      renderCell: (p) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">{p.value || 'N/A'}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">{p.row.qpayCenterCustomId}</Typography>
        </Box>
      )
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 140,
      sortable: false,
      renderCell: () => (
        <Chip icon={<FontAwesomeIcon icon={faArrowDown} />} label="SETTLEMENT" size="small" color="error" variant="filled" />
      )
    },
    {
      field: 'settledAmount',
      headerName: 'Amount',
      width: 150,
      renderCell: (p) => (
        <Typography color="error.main" fontWeight="bold">- {formatCurrency(p.value)}</Typography>
      )
    },
    {
      field: 'transactionId',
      headerName: 'Reference',
      width: 190,
      renderCell: (p) => <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value || '-'}</Typography>
    },
    { field: 'remarks', headerName: 'Remarks', width: 260 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: false,
      renderCell: () => <Chip label="COMPLETED" size="small" color="primary" variant="outlined" />
    }
  ];

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ padding: '20px' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              <FontAwesomeIcon icon={faWallet} style={{ marginRight: '10px', color: '#1976d2' }} />
              Q-Pay Centre Settlements Report
            </Typography>
          </Box>

          {/* Info block */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1">
              {centreInfo ? (
                <>
                  <strong>{centreInfo.name}</strong> ({centreInfo.customId}
                  {centreInfo.mobile ? ` | ${centreInfo.mobile}` : ''}
                  {centreInfo.email ? ` | ${centreInfo.email}` : ''})
                </>
              ) : (
                <>
                  <strong>All Q-Pay Centres</strong> ({centresList.length}) | Total Settled: <strong>{formatCurrency(summary.total)}</strong>
                </>
              )}
              {' '}| Period: {format(startDate, 'dd/MM/yyyy')} to {format(endDate, 'dd/MM/yyyy')}
            </Typography>
          </Alert>

          {/* Filter Controls */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Q-Pay Centre ID, name, transaction ID, remarks"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Q-Pay Centre</InputLabel>
                <Select value={selectedCentreFilter} label="Q-Pay Centre" onChange={(e) => setSelectedCentreFilter(e.target.value)}>
                  <MenuItem value="ALL">All Q-Pay Centres</MenuItem>
                  {centresList.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.customId} - {c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd/MM/yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd/MM/yyyy"
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <Button variant="contained" color="primary" onClick={fetchSettlementsReport}
                startIcon={<FontAwesomeIcon icon={faRefresh} />} fullWidth disabled={loading}>
                Refresh
              </Button>
            </Grid>
            {can('qpayCentre.settlementsReport.export') && (
              <Grid item xs={6} sm={1}>
                <Button variant="contained" color="success" onClick={handleExportCSV}
                  startIcon={<FontAwesomeIcon icon={faDownload} />} fullWidth disabled={filteredSettlements.length === 0}>
                  CSV
                </Button>
              </Grid>
            )}
          </Grid>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="error.main">Total Settled / Paid Out</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{formatCurrency(summary.total)}</Typography>
                  <Typography variant="body2" color="text.secondary">{summary.count} payouts</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">Q-Pay Centres Settled</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{summary.centreCount}</Typography>
                  <Typography variant="body2" color="text.secondary">of {centresList.length} Q-Pay Centre(s)</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">Total Records</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{filteredSettlements.length}</Typography>
                  <Typography variant="body2" color="text.secondary">of {settlements.length} in range</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Data Grid */}
          <Paper elevation={3} sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              rowHeight={56}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: () => (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="h6" color="text.secondary">No settlement records found</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Try adjusting your date range or filters</Typography>
                </Box>
              ) }}
              sx={{ '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' } }}
            />
          </Paper>
        </div>
      </LocalizationProvider>
    </Box>
  );
};

export default QpayCenterSettlementsReport;
